import React, { useEffect, useRef, useState, useCallback } from 'react';
import './Pricing.css';
import { useNavigate } from "react-router-dom";

const PLANS = [
  {
    name: 'Starter',
    inrPrice: '₹4,500',
    usdPrice: '$75',
    period: '',
    color: 'blue',
    badge: null,
    desc: 'Perfect for individuals exploring AI hiring and simplifying their recruitment process.',
    features: [
      '10 Interviews (approx.)',
      '1 AI JD Generation (approx.)',
      '1 JD Uploads (approx.)',
      '10 Resume Parsing (approx.)',
      '10 Candidate Matching (approx.)',
    ],
    cta: '1 Month Free Trial',
    ctaStyle: 'outline',
  },
  {
    name: 'Basic',
    inrPrice: '₹21,000',
    usdPrice: '$350',
    period: '',
    color: 'blue',
    badge: null,
    desc: 'For the startup teams looking to automate their hiring process.',
    features: [
      '50 Interviews (approx.)',
      '5 AI JD Generation (approx.)',
      '5 JD Uploads (approx.)',
      '50 Resume Parsing (approx.)',
      '50 Candidate Matching (approx.)',
    ],
    cta: '1 Month Free Trial',
    ctaStyle: 'outline',
  },
  {
    name: 'Standard',
    inrPrice: '₹1,00,000',
    usdPrice: '$1,650',
    period: '',
    color: 'featured',
    badge: 'Most Popular',
    desc: 'For growing teams running multiple pipelines.',
    features: [
      '250 Interviews (approx.)',
      '25 AI JD Generation (approx.)',
      '25 JD Uploads (approx.)',
      '250 Resume Parsing (approx.)',
      '250 Candidate Matching (approx.)',
    ],
    cta: '1 Month Free Trial',
    ctaStyle: 'solid',
  },
  {
    name: 'Premium',
    inrPrice: '₹3,75,000',
    usdPrice: '$6,200',
    period: '',
    color: 'green',
    badge: null,
    desc: 'Tailored for large organisations & agencies.',
    features: [
      '1,000 Interviews (approx.)',
      '100 AI JD Generation (approx.)',
      '100 JD Uploads (approx.)',
      '1,000 Resume Parsing (approx.)',
      '1,000 Candidate Matching (approx.)',
    ],
    cta: '1 Month Free Trial',
    ctaStyle: 'outline',
  },
];

export default function Pricing() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const [isUSA, setIsUSA] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  const handleDotsClick = useCallback((e, index) => {
    e.stopPropagation();
    setOpenDropdown((prev) => (prev === index ? null : index));
  }, []);

  useEffect(() => {
    const close = () => setOpenDropdown(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            observer.unobserve(e.target);
          }
        }),
      { threshold: 0.1 }
    );

    ref.current?.querySelectorAll('.price-card').forEach((el) =>
      observer.observe(el)
    );

    return () => observer.disconnect();
  }, []);

  return (
    <div id="pricing" className="marketing-theme" ref={ref} style={{ scrollMarginTop: '40px' }}>
      {/* Header */}
      <div className="feat-header">
        <div className="section-label"></div>
        <h2 className="section-title">Simple, Transparent Pricing</h2>
        
        <p className="section-sub">
          No hidden fees. No surprises. Pick the bundle that fits your team and
          scale whenever you're ready.
        </p>

        {/* Region Toggle */}
        <div className="pricing-toggle-row">
          <span className={`pricing-toggle-label ${!isUSA ? 'active' : ''}`}>
            <img 
              src="https://flagcdn.com/in.svg" 
              alt="India" 
              style={{ width: "18px", marginRight: "6px" }} 
            />
            India
          </span>
          <div
            className={`pricing-toggle-track ${isUSA ? 'usa' : ''}`}
            onClick={() => setIsUSA((v) => !v)}
          >
            <div className="pricing-toggle-thumb" />
          </div>
          <span className={`pricing-toggle-label ${isUSA ? 'active' : ''}`}>
           🌐 Others
          </span>
        </div>
        
        <p className="pricing-no-expiry" style={{ marginTop: "18px" }}>
          ✦ All plans are usage-based, no time limits, no expiry. Use at your own pace.
        </p>
      </div>

      {/* ── Introductory Pricing Banner ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '32px',
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          background: 'linear-gradient(135deg, rgba(250,204,21,0.12) 0%, rgba(251,146,60,0.10) 100%)',
          border: '1px solid rgba(250,204,21,0.35)',
          borderRadius: '100px',
          padding: '10px 22px',
          boxShadow: '0 0 24px rgba(250,204,21,0.08)',
        }}>
          {/* Pulsing dot */}
          <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
            <span style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#facc15',
              boxShadow: '0 0 0 0 rgba(250,204,21,0.6)',
              animation: 'introPulse 1.8s ease-out infinite',
            }} />
          </span>
          <span style={{
            fontSize: '0.82rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            color: '#fde68a',
          }}>
            Introductory Pricing
          </span>
          <span style={{
            fontSize: '0.78rem',
            color: 'rgba(253,230,138,0.65)',
            fontWeight: 400,
          }}>
           Lock-in these rates before they change
          </span>
        </div>
      </div>

      {/* Keyframe for pulse — injected via a style tag */}
      <style>{`
        @keyframes introPulse {
          0%   { box-shadow: 0 0 0 0 rgba(250,204,21,0.6); }
          60%  { box-shadow: 0 0 0 7px rgba(250,204,21,0); }
          100% { box-shadow: 0 0 0 0 rgba(250,204,21,0); }
        }
      `}</style>

      {/* Cards */}
      <div className="price-grid">
        {PLANS.map((plan, i) => (
          <div
            key={plan.name}
            className={`price-card price-card-${plan.color}${
              plan.color === 'featured' ? ' price-card-featured' : ''
            }`}
            style={{ '--delay': `${i * 0.1}s` }}
          >
            {/* Badge */}
            {plan.badge && (
              <div className="price-badge">{plan.badge}</div>
            )}

            {/* Plan name + dots menu */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="price-name">{plan.name}</div>

              <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={(e) => handleDotsClick(e, i)}
                  style={{
                    background: openDropdown === i ? 'rgba(129, 140, 248, 0.15)' : 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    cursor: 'pointer',
                    padding: '6px 10px',
                    borderRadius: '8px',
                    color: openDropdown === i ? '#818cf8' : 'rgba(255,255,255,0.8)',
                    fontSize: '1.2rem',
                    letterSpacing: '2px',
                    lineHeight: 1,
                    fontFamily: 'inherit',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(129, 140, 248, 0.2)';
                    e.currentTarget.style.color = '#a5b4fc';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      openDropdown === i ? 'rgba(129, 140, 248, 0.15)' : 'rgba(255,255,255,0.08)';
                    e.currentTarget.style.color =
                      openDropdown === i ? '#818cf8' : 'rgba(255,255,255,0.8)';
                  }}
                >
                  ⋯
                </button>

                {openDropdown === i && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      right: 0,
                      background: '#1a1f2e',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: '10px',
                      padding: '4px',
                      minWidth: '145px',
                      zIndex: 30,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                    }}
                  >
                    {[
                      {
                        label: 'View details',
                        icon: (
                          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M2 8h12M8 2l6 6-6 6" />
                          </svg>
                        ),
                      },
                      // {
                      //   label: 'Compare plans',
                      //   icon: (
                      //     <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                      //       <path d="M3 5h10M3 8h7M3 11h5" />
                      //     </svg>
                      //   ),
                      // },
                    ].map((item) => (
                      <div
                        key={item.label}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 10px',
                          borderRadius: '7px',
                          fontSize: '0.77rem',
                          color: 'rgba(255,255,255,0.65)',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          transition: 'background 0.12s, color 0.12s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(99,102,241,0.18)';
                          e.currentTarget.style.color = '#a5b4fc';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                          e.currentTarget.style.color = 'rgba(255,255,255,0.65)';
                        }}
                        onClick={() => {
                          setOpenDropdown(null);
                          if (item.label === 'View details') {
                            navigate(`/pricing-detail?plan=${plan.name}`);
                          }
                          // if (item.label === 'Compare plans') {
                          //   navigate(`/pricing-detail?view=compare&plan=${plan.name}`);
                          // }
                        }}
                      >
                        {item.icon}
                        {item.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Price + Introductory tag */}
            <div className="price-amount" style={{ alignItems: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
              <span className="price-value">
                {isUSA ? plan.usdPrice : plan.inrPrice}
              </span>
              {plan.period && (
                <span className="price-period">{plan.period}</span>
              )}
              {/* Introductory pill on each card */}
              <span style={{
                display: 'inline-block',
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#fbbf24',
                background: 'rgba(251,191,36,0.13)',
                border: '1px solid rgba(251,191,36,0.3)',
                borderRadius: '6px',
                padding: '2px 7px',
                marginBottom: '3px',
                whiteSpace: 'nowrap',
              }}>
                Intro price
              </span>
            </div>

            <p className="price-desc">{plan.desc}</p>

            {/* Features */}
            <ul className="price-features">
              {plan.features.map((f) => {
                const isInterview = f.toLowerCase().includes('interview');
                return (
                  <li key={f}>
                    <span className="price-check">✔</span>
                    {isInterview ? (
                      <span className="highlight-interview">{f}</span>
                    ) : (
                      f
                    )}
                  </li>
                );
              })}
            </ul>

            {/* CTA */}
            <button
              className={`price-cta price-cta-${plan.ctaStyle}${
                plan.color === 'featured' ? ' price-cta-featured' : ''
              }`}
              onClick={() => {
                if (plan.cta === '1 Month Free Trial' || plan.cta === 'Start Free Trial') {
                  navigate('/tenantRegister');
                }
              }}
            >
              {plan.cta}
            </button>

            {/* Glow */}
            <div
              className={`feat-glow glow-${
                plan.color === 'featured' ? 'blue' : plan.color
              }`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}